import { createFileRoute, redirect } from '@tanstack/react-router';
import { HostEvent, Page } from '@thoughtspot/visual-embed-sdk/react';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { getOffset, PrerenderedAppEmbedContext } from '@/components/Thoughtspot/PreRenderedAppEmbed';
import { PAGE_IDS } from '@/constants/pages';
import { pageIdToThoughtSpotPage } from '@/customizations/navigationItems';

type RouteSearch = {
  reload?: number;
};

type RouteParams = {
  page: string | Page;
};

/**
 * Parses and validates the page parameter
 * @param page - The page ID to parse
 * @returns The corresponding ThoughtSpot page
 * @throws Error if page is invalid
 */
const parsePageParameter = (page: string): Page => {
  const pageId = Object.values(PAGE_IDS).find((id) => id === page);
  if (!pageId) {
    throw new Error('Invalid page');
  }

  const thoughtSpotPage = pageIdToThoughtSpotPage[page] || page;

  if (!Object.values(Page).includes(thoughtSpotPage as Page)) {
    throw new Error(`Invalid ThoughtSpot page: ${thoughtSpotPage}`);
  }

  return thoughtSpotPage as Page;
};

export const Route = createFileRoute('/dashboard/_thoughtspot/$page')({
  component: RouteComponent,
  validateSearch: (search: Record<string, number>): RouteSearch => ({
    reload: search.reload ? (search.reload as number) : undefined,
  }),
  params: {
    parse: (params: Record<string, string>): RouteParams => ({
      page: parsePageParameter(params.page),
    }),
  },
  beforeLoad: ({ params }) => {
    // prevent this route from doing the data TS load
    if (params.page === 'data-source') {
      throw redirect({ to: '/dashboard/data-source/$tab', params: { tab: 'datasets' } });
    }
  },
});

/**
 * Dashboard route component that embeds ThoughtSpot Pages
 */
function RouteComponent() {
  const { page } = Route.useParams();
  const { reload } = Route.useSearch();

  const ref = useRef<HTMLDivElement>(null);
  const { initializeView, embedRef } = useContext(PrerenderedAppEmbedContext);

  const handleReload = useCallback(async () => {
    try {
      await embedRef.current?.trigger(HostEvent.Reload);
    } catch (error) {
      console.error('Failed to reload:', error);
    }
  }, [embedRef]);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const coords = ref.current.getBoundingClientRect();
    const offset = getOffset(ref.current);
    const coordsData = {
      height: coords.height,
      width: coords.width,
      top: offset.top,
      left: offset.left,
    };

    initializeView(page, coordsData);

    // Trigger reload if specified in URL params
    if (reload) {
      void handleReload();
    }
  }, [page, reload, handleReload, initializeView]);

  return <div ref={ref}></div>;
}
